import { jsPDF } from "jspdf";
import type { Budget } from "@/hooks/useBudgets";
import { barterSplit, recurringBilling } from "@/lib/barter";
import logoUrl from "@/assets/brand/simbolo.png";
import { formatBRL } from "@/lib/format";

const BRAND = {
  name: "Eliveuton Souza",
  tagline: "Desenvolvimento Web & Automação com IA",
  email: "eliveuton.dev@gmail.com",
  whatsapp: "+55 (38) 98846-7524",
};

// Corporate dark palette matching site
const COLOR = {
  primary: [88, 28, 135] as [number, number, number], // purple-900
  accent: [139, 92, 246] as [number, number, number], // violet-500
  dark: [17, 24, 39] as [number, number, number], // gray-900
  muted: [107, 114, 128] as [number, number, number], // gray-500
  light: [243, 244, 246] as [number, number, number], // gray-100
  border: [229, 231, 235] as [number, number, number], // gray-200
};

const PLATFORM_LABELS: Record<string, string> = {
  direct: "Cliente Direto",
  workana: "Workana",
  other: "Outra Plataforma",
};

export const fmtBRL = (v: number) => formatBRL(v);

export const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 50) || "proposta";

async function loadImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateBudgetPdf(budget: Budget): Promise<void> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  let y = 0;

  // ===== HEADER BAND =====
  doc.setFillColor(...COLOR.dark);
  doc.rect(0, 0, pageW, 110, "F");
  doc.setFillColor(...COLOR.accent);
  doc.rect(0, 110, pageW, 3, "F");

  // Logo
  const logo = await loadImageAsDataUrl(logoUrl);
  if (logo) {
    try {
      doc.addImage(logo, "PNG", margin, 28, 54, 54);
    } catch {
      /* ignore */
    }
  }

  // Brand text
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(BRAND.name, margin + 70, 52);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(200, 200, 220);
  doc.text(BRAND.tagline, margin + 70, 68);

  // Contact right
  doc.setFontSize(9);
  doc.setTextColor(220, 220, 235);
  const right = pageW - margin;
  doc.text(BRAND.email, right, 50, { align: "right" });
  doc.text(BRAND.whatsapp, right, 64, { align: "right" });
  const today = new Date().toLocaleDateString("pt-BR");
  doc.text(`Emitida em ${today}`, right, 78, { align: "right" });

  y = 150;

  // ===== TITLE =====
  doc.setTextColor(...COLOR.muted);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("PROPOSTA COMERCIAL", margin, y);
  y += 22;
  doc.setTextColor(...COLOR.dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  const titleLines = doc.splitTextToSize(budget.title, pageW - margin * 2);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 26 + 8;

  // ===== CLIENT BOX =====
  if (budget.lead) {
    doc.setFillColor(...COLOR.light);
    doc.roundedRect(margin, y, pageW - margin * 2, 56, 6, 6, "F");
    doc.setTextColor(...COLOR.muted);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("PREPARADA PARA", margin + 16, y + 18);
    doc.setTextColor(...COLOR.dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(budget.lead.name, margin + 16, y + 35);
    if (budget.lead.email) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...COLOR.muted);
      doc.text(budget.lead.email, margin + 16, y + 49);
    }
    y += 72;
  }

  // ===== SCOPE =====
  if (budget.description) {
    doc.setTextColor(...COLOR.dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Escopo do Projeto", margin, y);
    y += 6;
    doc.setDrawColor(...COLOR.accent);
    doc.setLineWidth(2);
    doc.line(margin, y, margin + 36, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 70);
    const descLines = doc.splitTextToSize(budget.description, pageW - margin * 2);
    doc.text(descLines, margin, y);
    y += descLines.length * 15 + 16;
  }

  // ===== SERVICES =====
  const items = budget.items ?? [];
  const serviceNames = items.map((i) => i.title).filter(Boolean);
  if (serviceNames.length > 0 || budget.service_type) {
    doc.setTextColor(...COLOR.muted);
    doc.setFontSize(9);
    doc.text(serviceNames.length > 1 ? "SERVIÇOS DO PACOTE" : "TIPO DE SERVIÇO", margin, y);
    doc.setTextColor(...COLOR.dark);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    const names = serviceNames.length > 0 ? serviceNames.join("  ·  ") : budget.service_type!.name;
    const nameLines = doc.splitTextToSize(names, pageW - margin * 2) as string[];
    doc.text(nameLines, margin, y + 14);
    y += 16 + nameLines.length * 14;
  }

  // ===== VALUES =====
  if (y > pageH - 280) {
    doc.addPage();
    y = margin;
  }

  doc.setTextColor(...COLOR.dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Investimento", margin, y);
  y += 6;
  doc.setDrawColor(...COLOR.accent);
  doc.setLineWidth(2);
  doc.line(margin, y, margin + 36, y);
  y += 18;

  const labelX = margin + 16;
  const valueX = pageW - margin - 16;
  const rowH = 22;

  // Detail rows: one per item, then subtotal/discount (packages), then fee.
  // Recurring items are excluded from itemsGross/discount — they're shown separately below, not part of the one-time deal.
  const oneTimeItems = items.filter((i) => i.pricing_mode !== "recurring");
  const itemsGross = oneTimeItems.reduce((s, i) => s + i.subtotal, 0);
  const discount = items.length > 0 ? Math.max(itemsGross - budget.gross_total, 0) : 0;
  const detailRows =
    Math.max(items.length, 1) +
    (items.length > 1 ? 1 : 0) +
    (discount > 0 ? 1 : 0) +
    (budget.platform_fee > 0 ? 1 : 0);
  doc.setFillColor(250, 250, 252);
  doc.roundedRect(margin, y, pageW - margin * 2, rowH * detailRows + 16, 6, 6, "F");
  y += 14;

  doc.setFontSize(11);
  const detailRow = (
    label: string,
    value: string,
    opts: { bold?: boolean; color?: [number, number, number] } = {},
  ) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 90);
    const labelLines = doc.splitTextToSize(label, pageW - margin * 2 - 32 - 120) as string[];
    doc.text(labelLines[0] ?? "", labelX, y + 4);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setTextColor(...(opts.color ?? COLOR.dark));
    doc.text(value, valueX, y + 4, { align: "right" });
    y += rowH;
  };

  if (items.length === 0) {
    detailRow(
      budget.pricing_mode === "hourly" && budget.hours && budget.hourly_rate
        ? `${budget.hours}h × ${fmtBRL(budget.hourly_rate)}/hora`
        : "Projeto com preço fechado",
      fmtBRL(budget.gross_total),
      { bold: true },
    );
  } else {
    for (const it of items) {
      const qty =
        it.pricing_mode === "hourly"
          ? `${it.hours ?? 0}h × ${fmtBRL(it.hourly_rate ?? 0)}/hora`
          : it.pricing_mode === "recurring"
            ? "mensalidade"
            : "preço fechado";
      const value =
        it.pricing_mode === "recurring" ? `${fmtBRL(it.subtotal)}/mês` : fmtBRL(it.subtotal);
      detailRow(`${it.title} — ${qty}`, value, { bold: items.length === 1 });
    }
    if (items.length > 1) detailRow("Subtotal dos serviços", fmtBRL(itemsGross), { bold: true });
    if (discount > 0)
      detailRow(`Desconto do pacote (${budget.discount_percent ?? ""}%)`, `- ${fmtBRL(discount)}`, {
        color: [30, 140, 90],
      });
  }

  if (budget.platform_fee > 0) {
    const platformLabel = PLATFORM_LABELS[budget.platform] || budget.platform;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 90);
    doc.text(`Taxa ${platformLabel} (${(budget.platform_fee * 100).toFixed(0)}%)`, labelX, y + 4);
    doc.setTextColor(180, 50, 50);
    doc.text(`- ${fmtBRL(budget.gross_total - budget.net_total)}`, valueX, y + 4, {
      align: "right",
    });
    y += rowH;
  }

  y += 8;

  // Total highlighted
  doc.setFillColor(...COLOR.primary);
  doc.roundedRect(margin, y, pageW - margin * 2, 50, 6, 6, "F");
  doc.setTextColor(220, 220, 240);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("VALOR TOTAL", labelX, y + 20);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(fmtBRL(budget.net_total), valueX, y + 30, { align: "right" });
  y += 70;

  // ===== PERMUTA =====
  const barter = barterSplit(budget.net_total, budget.barter_percent ?? 0);
  if (barter.barterTotal > 0) {
    const pct = Number.isInteger(barter.barterPercent)
      ? String(barter.barterPercent)
      : barter.barterPercent.toFixed(1);
    const descLines = budget.barter_description?.trim()
      ? (doc.splitTextToSize(budget.barter_description.trim(), pageW - margin * 2 - 32) as string[])
      : [];
    const boxH = 46 + (descLines.length > 0 ? 14 + descLines.length * 12 : 0);
    doc.setFillColor(...COLOR.light);
    doc.roundedRect(margin, y, pageW - margin * 2, boxH, 6, 6, "F");
    doc.setTextColor(...COLOR.muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`PERMUTA (${pct}%)`, margin + 16, y + 14);
    doc.text("VALOR A COBRAR", valueX - 16, y + 14, { align: "right" });
    doc.setTextColor(...COLOR.dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(`- ${fmtBRL(barter.barterTotal)}`, margin + 16, y + 30);
    doc.text(barter.isFull ? "R$ 0,00 (integral)" : fmtBRL(barter.cashTotal), valueX - 16, y + 30, {
      align: "right",
    });
    if (descLines.length > 0) {
      doc.setTextColor(...COLOR.muted);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("CONTRAPARTIDA", margin + 16, y + 48);
      doc.setTextColor(...COLOR.dark);
      doc.setFontSize(10);
      doc.text(descLines, margin + 16, y + 60);
    }
    y += boxH + 12;
  }

  // ===== RECURRING (mensalidade) =====
  if (budget.recurring_monthly_total > 0) {
    doc.setFillColor(...COLOR.light);
    doc.roundedRect(margin, y, pageW - margin * 2, 36, 6, 6, "F");
    doc.setTextColor(...COLOR.muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("MENSALIDADE", margin + 16, y + 14);
    doc.setTextColor(...COLOR.dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(
      recurringBilling(budget).inBarter
        ? `${fmtBRL(budget.recurring_monthly_total)}/mês — incluída na permuta`
        : `+ ${fmtBRL(budget.recurring_monthly_total)}/mês`,
      margin + 16,
      y + 28,
    );
    y += 48;
  }

  // ===== TIMELINE =====
  const hoursForTimeline =
    items.length > 0
      ? items.reduce((s, i) => s + (i.pricing_mode === "hourly" ? (i.hours ?? 0) : 0), 0)
      : budget.pricing_mode === "hourly"
        ? (budget.hours ?? 0)
        : 0;
  if (hoursForTimeline > 0) {
    const days = Math.ceil(hoursForTimeline / 6);
    doc.setTextColor(...COLOR.muted);
    doc.setFontSize(9);
    doc.text("PRAZO ESTIMADO", margin, y);
    doc.setTextColor(...COLOR.dark);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`${days} dias úteis`, margin, y + 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLOR.muted);
    doc.text("(considerando 6h produtivas/dia)", margin + 110, y + 16);
    y += 32;
  }

  // ===== INCLUDED =====
  if (y > pageH - 200) {
    doc.addPage();
    y = margin;
  }

  doc.setTextColor(...COLOR.dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("O que está incluso", margin, y);
  y += 6;
  doc.setDrawColor(...COLOR.accent);
  doc.setLineWidth(2);
  doc.line(margin, y, margin + 36, y);
  y += 18;

  const included = [
    "Desenvolvimento completo do escopo acordado",
    "Revisões e ajustes durante a execução",
    "Deploy e configuração em ambiente de produção",
    "Suporte pós-entrega por 7 dias",
    "Comunicação direta via WhatsApp ou e-mail",
  ];

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 70);
  included.forEach((item) => {
    doc.setTextColor(...COLOR.accent);
    doc.text("✓", margin, y);
    doc.setTextColor(60, 60, 70);
    doc.text(item, margin + 16, y);
    y += 18;
  });

  y += 12;

  // ===== VALIDITY =====
  doc.setFillColor(...COLOR.light);
  doc.roundedRect(margin, y, pageW - margin * 2, 36, 6, 6, "F");
  doc.setTextColor(...COLOR.muted);
  doc.setFontSize(9);
  doc.text("VALIDADE DA PROPOSTA", margin + 16, y + 14);
  doc.setTextColor(...COLOR.dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("15 dias a partir da data de emissão", margin + 16, y + 28);

  // ===== FOOTER (every page) =====
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(...COLOR.border);
    doc.setLineWidth(0.5);
    doc.line(margin, pageH - 40, pageW - margin, pageH - 40);
    doc.setTextColor(...COLOR.muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`${BRAND.name} — ${BRAND.tagline}`, margin, pageH - 24);
    doc.text(`${i} / ${pageCount}`, pageW - margin, pageH - 24, { align: "right" });
    doc.text(`${BRAND.email}  ·  ${BRAND.whatsapp}`, margin, pageH - 12);
  }

  const date = new Date().toISOString().slice(0, 10);
  doc.save(`proposta-${slugify(budget.title)}-${date}.pdf`);
}
